import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useId, useRef, useState } from 'react';
import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import { treasurySuggestions, treasuryColors } from './TreasuryCard';

function exactCents(euros: string): string | null {
  if (!/^\d+(?:[.,]\d{1,2})?$/u.test(euros)) return null;
  const [whole, decimal = ''] = euros.replace(',', '.').split('.');
  const cents = BigInt(whole!) * 100n + BigInt(decimal.padEnd(2, '0'));
  return cents <= 1000000000n ? cents.toString() : null;
}

export function TreasurySettingsDialog({
  overview,
  onCancel,
  onSave,
}: {
  readonly overview: FinancialOverview;
  readonly onCancel: () => void;
  readonly onSave: (thresholds: readonly string[], reserveCents?: string) => Promise<void>;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useId();
  const suggestions = treasurySuggestions(overview);
  const initial = overview.treasuryColorThresholdsCents ?? suggestions;
  const [drafts, setDrafts] = useState(() =>
    [0, 1, 2, 3].map((index) =>
      initial?.[index] == null ? '' : (Number(initial[index]) / 100).toFixed(2),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [reserveDraft, setReserveDraft] = useState(() =>
    overview.reserveTargetCents == null
      ? ''
      : (Number(overview.reserveTargetCents) / 100).toFixed(2),
  );
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const names = [
    'Jaune — soyez vigilant',
    'Bleu — tout va bien',
    'Vert — tréso saine',
    'Violet — excellent',
  ];
  async function save(): Promise<void> {
    const cents = drafts.map(exactCents);
    const reserveCents = reserveDraft === '' ? undefined : exactCents(reserveDraft);
    if (
      cents.some((value) => value === null) ||
      reserveCents === null ||
      reserveCents === '0' ||
      cents.some((value, index) => index > 0 && BigInt(value!) <= BigInt(cents[index - 1]!))
    ) {
      setError('Saisis quatre montants croissants, avec au maximum deux décimales.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(cents as string[], reserveCents);
      onCancel();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <ModalDialog
      ref={dialog}
      className="treasury-settings"
      aria-labelledby={heading}
      onCancel={onCancel}
      close={
        <ModalCloseButton className="tosho-modal-close" onClick={onCancel} aria-label="Fermer" />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <header className="tosho-modal-header">
          <h2 id={heading}>Couleurs de trésorerie</h2>
        </header>
        <p>Choisissez le solde bancaire à partir duquel chaque couleur s’applique.</p>
        <p className="treasury-settings__method">
          Préconisations de ChatGPT : repères de gestion fondés sur votre réserve et les dépenses
          observées. Ils restent ajustables ; ils ne garantissent pas les charges futures.
        </p>
        <div className="treasury-settings__reserve">
          <p>
            Préconisation de ChatGPT :{' '}
            {overview.remuneration?.suggestedReserveCents == null
              ? 'une réserve couvrant deux mois de dépenses usuelles'
              : `${remunerationMoney(overview.remuneration.suggestedReserveCents)}, soit deux mois de dépenses usuelles observées`}
            .
          </p>
          <label>
            Objectif de réserve
            <span>
              <input
                aria-label="Objectif de réserve"
                type="number"
                min="1"
                max="10000000"
                step=".01"
                value={reserveDraft}
                onChange={(event) => setReserveDraft(event.currentTarget.value)}
              />
              €
            </span>
          </label>
        </div>
        <div className="treasury-settings__row">
          <p>Préconisation de ChatGPT : signaler une alerte sous le seuil de vigilance.</p>
          <label>
            <i style={{ background: treasuryColors[0] }} />
            Rouge — inférieur à <output>{drafts[0] ? `${drafts[0]} €` : '—'}</output>
          </label>
        </div>
        {names.map((name, index) => (
          <div className="treasury-settings__row" key={name}>
            <p>
              Préconisation de ChatGPT :{' '}
              {suggestions
                ? `à partir de ${remunerationMoney(suggestions[index])}`
                : 'à préciser après consolidation de l’historique des dépenses'}
              .
            </p>
            <label>
              <i style={{ background: treasuryColors[index + 1] }} />
              {name}
              <span>
                <input
                  type="number"
                  min="0"
                  max="10000000"
                  step=".01"
                  value={drafts[index]}
                  required
                  autoFocus={index === 0}
                  onChange={(event) =>
                    setDrafts(
                      drafts.map((value, offset) =>
                        offset === index ? event.currentTarget.value : value,
                      ),
                    )
                  }
                />
                €
              </span>
            </label>
          </div>
        ))}
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <footer>
          <button type="button" disabled={saving} onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Valider'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
